import { useCallback, useEffect, useRef } from "react";
import { usePersistentStore } from "@/stores/playerStore";

// Synthesizes a classic CRT channel-switch sound using the Web Audio API:
//   1. ~150ms burst of band-passed white noise (TV static)
//   2. a quick downward pitch glide ("click" / "thunk")
//   3. a tiny high-frequency chirp (signal lock)
// Total duration: ~220ms. No external audio assets needed.
//
// Volume is low per spec section #50: "默认音量极低". User can toggle in
// settings (soundFx) — this hook respects that flag from the persistent
// store and gracefully no-ops if disabled.

export function useChannelSound() {
  const ctxRef = useRef<AudioContext | null>(null);
  const unlockedRef = useRef(false);

  const ensureCtx = useCallback((): AudioContext | null => {
    if (typeof window === "undefined") return null;
    if (!ctxRef.current) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      ctxRef.current = new Ctor();
    }
    return ctxRef.current;
  }, []);

  // Many browsers require a user gesture to unlock the AudioContext.
  // We attempt to unlock it on the first keydown / pointerdown anywhere.
  useEffect(() => {
    const unlock = () => {
      const ctx = ensureCtx();
      if (ctx && ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      unlockedRef.current = true;
    };
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => {
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("pointerdown", unlock);
    };
  }, [ensureCtx]);

  const play = useCallback(() => {
    // Respect user toggle from settings
    const soundFx = usePersistentStore.getState().settings.soundFx;
    if (!soundFx) return;

    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);

    // --- 1. TV static noise burst (~150ms, band-passed, exponential decay) ---
    const noiseDuration = 0.15;
    const sampleRate = ctx.sampleRate;
    const bufferSize = Math.floor(noiseDuration * sampleRate);
    const noiseBuffer = ctx.createBuffer(1, bufferSize, sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      const t = i / sampleRate;
      const env = Math.pow(1 - t / noiseDuration, 2.5);
      data[i] = (Math.random() * 2 - 1) * env;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 3200;
    bp.Q.value = 0.9;

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.35, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + noiseDuration);

    noise.connect(bp).connect(noiseGain).connect(master);
    noise.start(now);
    noise.stop(now + noiseDuration + 0.02);

    // --- 2. Low-frequency click (channel detent) ---
    const click = ctx.createOscillator();
    click.type = "square";
    click.frequency.setValueAtTime(220, now);
    click.frequency.exponentialRampToValueAtTime(55, now + 0.08);

    const clickGain = ctx.createGain();
    clickGain.gain.setValueAtTime(0.0001, now);
    clickGain.gain.exponentialRampToValueAtTime(0.25, now + 0.005);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);

    click.connect(clickGain).connect(master);
    click.start(now);
    click.stop(now + 0.1);

    // --- 3. Brief high-frequency chirp (signal lock) at the end ---
    const chirp = ctx.createOscillator();
    chirp.type = "sine";
    chirp.frequency.setValueAtTime(1800, now + 0.13);
    chirp.frequency.exponentialRampToValueAtTime(2400, now + 0.21);

    const chirpGain = ctx.createGain();
    chirpGain.gain.setValueAtTime(0.0001, now + 0.13);
    chirpGain.gain.exponentialRampToValueAtTime(0.12, now + 0.15);
    chirpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

    chirp.connect(chirpGain).connect(master);
    chirp.start(now + 0.12);
    chirp.stop(now + 0.23);
  }, [ensureCtx]);

  return { play };
}